"use client";

import Link from "next/link";
import Image from "next/image";
import { ChevronRight, Percent, Package, Zap, Sparkles } from "lucide-react";
import { useEffect, useState, useRef } from "react";

interface DealProduct {
  id: string;
  name: string;
  image?: string;
}

interface DealGroup {
  id: string;
  name: string;
  type: 'sale' | 'bundle';
  products: DealProduct[];
  totalCount: number;
}

// Generate consistent brand-aligned gradient
function getDealStyle(name: string, type: string, index: number) {
  // Use brand green palette with subtle variations
  const colors = [
    { gradient: 'from-green-700 via-green-800 to-green-900', accent: 'bg-green-400' },
    { gradient: 'from-emerald-700 via-emerald-800 to-green-900', accent: 'bg-emerald-400' },
    { gradient: 'from-green-600 via-emerald-700 to-teal-800', accent: 'bg-green-300' },
    { gradient: 'from-teal-700 via-green-800 to-emerald-900', accent: 'bg-teal-400' },
  ];
  
  // Sale items get a subtle red accent
  if (type === 'sale') {
    return { gradient: 'from-green-800 via-green-900 to-gray-900', accent: 'bg-red-400' };
  }
  
  return colors[index % colors.length];
}

// Parse deal to get display info
function parseDealInfo(name: string) {
  const percentMatch = name.match(/(\d+)%/);
  const priceMatch = name.match(/(\d+)\s*(?:FOR|for)\s*\$(\d+)/);
  const dollarMatch = name.match(/\$(\d+)/);
  
  if (percentMatch) {
    return { type: 'percent', value: `${percentMatch[1]}%`, label: 'OFF' };
  }
  if (priceMatch) {
    return { type: 'bundle', value: priceMatch[1], price: `$${priceMatch[2]}`, label: 'FOR' };
  }
  if (dollarMatch) {
    return { type: 'price', value: `$${dollarMatch[1]}`, label: 'DEAL' };
  }
  return null;
}

export default function ShopSpecialsSection() {
  const [deals, setDeals] = useState<DealGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    async function fetchDeals() {
      try {
        const res = await fetch('/api/specials/grouped');
        if (res.ok) {
          const data = await res.json();
          if (data.success && data.deals) {
            setDeals(data.deals);
          }
        }
      } catch (error) {
        console.error('Failed to fetch deals:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchDeals();
  }, []);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = 300;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  if (loading) {
    return (
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <div className="h-6 w-24 bg-gray-200 rounded animate-pulse"></div>
        </div>
        <div className="flex gap-3 overflow-hidden">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="flex-shrink-0 w-[200px] sm:w-64 h-36 sm:h-44 bg-gray-200 rounded-xl animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  if (deals.length === 0) return null;

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold text-gray-900">Specials</h2>
        <Link href="/specials" className="text-sm text-primary_green hover:underline flex items-center font-medium">
          View All <ChevronRight className="h-4 w-4" />
        </Link>
      </div>
      
      {/* Scrollable container */}
      <div className="relative group/scroll">
        {/* Scroll buttons */}
        {deals.length > 3 && (
          <>
            <button 
              onClick={() => scroll('left')}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 shadow-lg rounded-full p-2 opacity-0 group-hover/scroll:opacity-100 transition-opacity hover:bg-white"
            >
              <ChevronRight className="h-5 w-5 rotate-180" />
            </button>
            <button 
              onClick={() => scroll('right')}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 shadow-lg rounded-full p-2 opacity-0 group-hover/scroll:opacity-100 transition-opacity hover:bg-white"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </>
        )}
        
        <div 
          ref={scrollRef}
          className="flex gap-3 overflow-x-auto scrollbar-hide pb-2"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {deals.map((deal, index) => {
            const style = getDealStyle(deal.name, deal.type, index);
            const dealInfo = parseDealInfo(deal.name);
            
            return (
              <Link
                key={deal.id}
                href={`/specials/${deal.id}`}
                className={`group flex-shrink-0 w-[200px] sm:w-64 rounded-xl overflow-hidden hover:shadow-2xl transition-all hover:-translate-y-1 bg-gradient-to-br ${style.gradient} relative`}
              >
                {/* Decorative elements */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-black/10 rounded-full translate-y-1/2 -translate-x-1/2"></div>
                <div className={`absolute top-3 right-3 w-2 h-2 ${style.accent} rounded-full animate-pulse`}></div>
                
                {/* Content */}
                <div className="relative z-10 p-3 sm:p-4 h-full flex flex-col">
                  {/* Deal Badge */}
                  {dealInfo && (
                    <div className="mb-1.5 sm:mb-2">
                      {dealInfo.type === 'percent' ? (
                        <div className="inline-flex items-center gap-1 bg-white/20 backdrop-blur-sm rounded-full px-2 py-0.5 sm:px-2.5 sm:py-1">
                          <Percent className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-white" />
                          <span className="text-white font-bold text-xs sm:text-sm">{dealInfo.value} {dealInfo.label}</span>
                        </div>
                      ) : dealInfo.type === 'bundle' ? (
                        <div className="inline-flex items-center gap-1 bg-white/20 backdrop-blur-sm rounded-full px-2 py-0.5 sm:px-2.5 sm:py-1">
                          <Package className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-white" />
                          <span className="text-white font-bold text-xs sm:text-sm">{dealInfo.value} for {dealInfo.price}</span>
                        </div>
                      ) : (
                        <div className="inline-flex items-center gap-1 bg-white/20 backdrop-blur-sm rounded-full px-2 py-0.5 sm:px-2.5 sm:py-1">
                          <Zap className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-white" />
                          <span className="text-white font-bold text-xs sm:text-sm">{dealInfo.value}</span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Title */}
                  <h3 className="text-white font-bold text-xs sm:text-sm leading-tight line-clamp-2 flex-grow">
                    {deal.name}
                  </h3>
                  
                  {/* Product Images */}
                  <div className="mt-2 sm:mt-3 flex items-center gap-1">
                    {deal.products.slice(0, 3).map((product) => (
                      <div 
                        key={product.id} 
                        className="w-8 h-8 sm:w-10 sm:h-10 relative bg-white rounded-md overflow-hidden flex-shrink-0 shadow-md"
                      >
                        {product.image ? (
                          <Image
                            src={product.image}
                            alt={product.name}
                            fill
                            className="object-contain p-0.5"
                            sizes="40px"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gray-50">
                            <span className="text-[10px]">🌿</span>
                          </div>
                        )}
                      </div>
                    ))}
                    {deal.totalCount > 3 && (
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 backdrop-blur-sm rounded-md flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-[10px] sm:text-xs font-bold">+{deal.totalCount - 3}</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Shop Button */}
                  <div className="mt-2 sm:mt-3 flex items-center justify-between">
                    <span className="text-white/60 text-[10px] sm:text-xs">{deal.totalCount} items</span>
                    <div className="inline-flex items-center gap-0.5 sm:gap-1 bg-white text-gray-900 text-[10px] sm:text-xs font-semibold px-2 py-1 sm:px-3 sm:py-1.5 rounded-full group-hover:bg-gray-100 transition-colors shadow-lg">
                      SHOP
                      <ChevronRight className="h-2.5 w-2.5 sm:h-3 sm:w-3 group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
